import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import getValue from 'get-value';
import stripNarrationText from './stripNarrationText.js';
import {
  ensureJitCacheWarm,
  getAudioJitCacheBaseUrl,
  resolveJitConfig,
  runJITSynthesis,
  type CacheSettings,
  type EnsureJitCacheWarmResult,
  type JitResolvedConfig
} from './audioJit';
import {
  normalizeAudioJitCacheBaseUrl,
  resolveJitAudioCacheKey
} from './audioJitCacheKey';
import { isJitAudioBusy } from './jitSynthesisInFlight';
import { jitAudioLog } from './jitAudioLog';
import {
  buildJitOverrides,
  extractAudioAiJitConfig,
  getLocaleJitEntry,
  type CourseAudioJitProviderConfig
} from './audioJitCourseConfig';
import useContent from '../stores/useContent';
import useSuspend from './useSuspend';
import useTranslations from '../stores/useTranslations';
import { get } from './SCORM.js';
import getQueryParameter from './getQueryParameter';

/**
 * Special author/export launch modes (audio script, word doc, AuthorAudioJIT
 * runtime bridge). The whole course renders at once in these modes, so automatic
 * playback-driven JiT synthesis must NOT fire — clips are only generated on
 * explicit request (AuthorAudioJIT → `warmNarrationCache`, a separate path).
 */
const SPECIAL_AUDIO_MODE =
  Boolean(getQueryParameter('audioDoc')) ||
  Boolean(getQueryParameter('ucAuthorAudio')) ||
  Boolean(getQueryParameter('word')) ||
  /(^|[+ ])worddoc([+ ]|$)/.test(getQueryParameter('tags') || '');

interface NarrationPayload {
  screenId: string;
  key: string;
  text: string;
}

const runtimeAudioCache = new Map<string, Promise<string | null>>();
const getCacheBaseUrl = (
  config?: CourseAudioJitProviderConfig | null
): string =>
  normalizeAudioJitCacheBaseUrl(config?.cacheBaseUrl) ||
  getAudioJitCacheBaseUrl();

export const cleanNarrationText = (text?: string | number | null) => {
  if (text === null || text === undefined) return '';

  const withLineBreaks = String(text)
    .replace(/\{r\}/g, '\n\n')
    .replace(/\{n\}/g, '\n');

  return stripNarrationText(withLineBreaks)
    .replace(/\s+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ');
};

export default function useAudioNarration() {
  const contentStore = useContent();
  const { compiled } = storeToRefs(contentStore);
  const suspend = useSuspend();
  const translations = useTranslations();

  /** `useSuspend()` uses `inject` and is undefined when this composable runs outside setup (e.g. async after setTimeout). */
  const narrationLocale = computed(
    () => suspend?.locale ?? get('locale') ?? 'en-GB'
  );

  const audioAiJitConfig = computed<JitResolvedConfig | null>(() => {
    const rawConfig = extractAudioAiJitConfig(
      compiled.value as Record<string, unknown> | undefined
    );
    const localeConfig = getLocaleJitEntry(rawConfig, narrationLocale.value);

    if (!localeConfig) return null;

    try {
      return resolveJitConfig(
        buildJitOverrides(localeConfig, narrationLocale.value)
      );
    } catch {
      return null;
    }
  });

  const audioAiJitCacheBaseUrl = computed<string>(() => {
    const rawConfig = extractAudioAiJitConfig(
      compiled.value as Record<string, unknown> | undefined
    );
    const localeConfig = getLocaleJitEntry(rawConfig, narrationLocale.value);

    if (!localeConfig || localeConfig === true) {
      return getAudioJitCacheBaseUrl();
    }

    return getCacheBaseUrl(localeConfig);
  });

  const audioAiJitEnabled = computed(() => Boolean(audioAiJitConfig.value));

  const translate = (
    key?: string | number | null,
    fallback?: string | number | null
  ) => {
    return cleanNarrationText(
      translations.translate(narrationLocale.value, key, fallback)
    );
  };

  const fillTemplates = (
    text: string,
    values: Record<string, string | number> = {}
  ) => {
    return text.replace(/\$\{([^}]+)\}/g, (_, rawPath) => {
      const [path, defaultValue] = String(rawPath).split(/, */);
      const value = getValue(values, path);

      if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
      }

      return defaultValue || '';
    });
  };

  const resolveText = (
    key?: string | number | null,
    fallback?: string | number | null,
    values: Record<string, string | number> = {}
  ) => {
    const translated = translate(key, fallback);
    return cleanNarrationText(fillTemplates(translated, values));
  };

  const resolveNarrationAudio = async (
    payload: NarrationPayload | null
  ): Promise<string | null> => {
    if (!payload?.text || !payload?.screenId || !payload?.key) {
      return null;
    }

    // No automatic playback synthesis in author/export modes (see SPECIAL_AUDIO_MODE).
    if (SPECIAL_AUDIO_MODE) {
      return null;
    }

    if (!audioAiJitConfig.value) {
      return null;
    }

    const providerConfig = audioAiJitConfig.value;
    const cacheSettings: CacheSettings = {
      allowLocalRuntime: true,
      allowRead: true,
      allowWrite: true
    };

    let cacheKey = '';
    try {
      cacheKey = await resolveJitAudioCacheKey({
        provider: providerConfig.provider,
        model: providerConfig.model,
        voice: providerConfig.voice,
        locale: providerConfig.locale,
        voiceLocale: providerConfig.voiceLocale,
        text: payload.text
      });
    } catch (_error) {
      cacheKey = JSON.stringify([
        providerConfig.provider,
        providerConfig.model,
        providerConfig.voice,
        providerConfig.locale,
        payload.text
      ]);
    }

    if (!runtimeAudioCache.has(cacheKey) || !cacheSettings.allowLocalRuntime) {
      jitAudioLog('playback: resolve', payload.key);
      runtimeAudioCache.set(
        cacheKey,
        (async () => {
          try {
            const result = await runJITSynthesis({
              text: payload.text,
              providerConfig,
              cacheSettings,
              cacheBaseUrl: audioAiJitCacheBaseUrl.value
            });

            return result.audioSrc || null;
          } catch (error) {
            console.error('Failed to resolve runtime AI audio', error);
            return null;
          }
        })()
      );
    }

    return runtimeAudioCache.get(cacheKey) as Promise<string | null>;
  };

  const warmNarrationCache = async (
    text: string,
    logLabel?: string,
    allowRead = true,
    allowWrite = true
  ): Promise<EnsureJitCacheWarmResult | undefined> => {
    if (!text.trim() || !audioAiJitConfig.value) return;

    return ensureJitCacheWarm({
      text,
      providerConfig: audioAiJitConfig.value,
      cacheSettings: { allowRead, allowWrite },
      cacheBaseUrl: audioAiJitCacheBaseUrl.value,
      logLabel
    });
  };

  return {
    audioAiJitEnabled,
    audioAiJitConfig,
    audioAiJitCacheBaseUrl,
    resolveText,
    resolveNarrationAudio,
    warmNarrationCache
  };
}

export { isJitAudioBusy };
